import * as reduxApi from 'src/utils/redux-api-v2';
import type {Dispatch, ThunkAction} from 'src/reducers';
import {ANALYTICS_API_V3} from 'src/utils/analytics-api-migration';
import type {ApiOptions, ApiPostOptions} from 'src/utils/redux-api';

import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';

import type {
  SavedDashboardList,
  CurrentReferralFilters,
  SelectedReferralsFilterColumnsData,
} from 'src/types/analytics-genesis';

import {thunkify as flow} from 'src/utils/thunks';

import {fetching, key} from 'src/utils/redux';

import {
  referralsOverviewTableColumnsQuery,
  programsOverviewTableColumnsQuery,
  jobsOverviewTableColumnsQuery,
  payoutsOverviewTableColumnsQuery,
} from 'src/utils/analytics-genesis';


const analyticsOptions: ApiOptions = {apiPath: ANALYTICS_API_V3};
const analyticsApiOptions: ApiPostOptions = {apiPath: ANALYTICS_API_V3};

export const SET_SELECTED_DATE_RANGE =
  'analytics-referrals/set-selected-date-range';
export const RECEIVE_SAVED_DASHBOARDS =
  'analytics-referrals/receive-saved-dashboards';
export const SET_CURRENT_DASHBOARD_ID =
  'analytics-referrals/set-current-dashboard-id';
export const SET_SELECTED_REFERRALS_FILTERS =
  'analytics-referrals/set-selected-referrals-filters';
export const CLEAR_SELECTED_FILTERS =
  'analytics-referrals/clear-selected-filters';

export const RECEIVE_REFERRALS_OVERVIEW_TABLE_COLUMNS =
  'analytics-referrals/receive-referrals-overview-table-columns';

export const RECEIVE_PROGRAMS_OVERVIEW_TABLE_COLUMNS =
  'analytics-referrals/receive-programs-overview-table-columns';

export const RECEIVE_JOBS_OVERVIEW_TABLE_COLUMNS =
  'analytics-referrals/receive-jobs-overview-table-columns';

export const RECEIVE_PAYOUTS_OVERVIEW_TABLE_COLUMNS =
  'analytics-referrals/receive-payouts-overview-table-columns';

type SetDateRangeAction = {
  type: 'analytics-referrals/set-selected-date-range';
  payload: DateRangeWithTimezone;
};

type ReceiveSavedDashboardsAction = {
  type: 'analytics-referrals/receive-saved-dashboards';
  payload: SavedDashboardList;
};

type SetCurrentDashboardIdAction = {
  type: 'analytics-referrals/set-current-dashboard-id';
  payload: string | null | undefined;
};

type SetSelectedReferralFiltersAction = {
  type: 'analytics-referrals/set-selected-referrals-filters';
  payload: CurrentReferralFilters;
};

type ClearSelectedFiltersAction = {
  type: 'analytics-referrals/clear-selected-filters';
};

type ReceiveReferralsOverviewTableColumnAction = {
  type: 'analytics-referrals/receive-referrals-overview-table-columns';
  payload: SelectedReferralsFilterColumnsData;
};

type ReceiveProgramsOverviewTableColumnAction = {
  type: 'analytics-referrals/receive-programs-overview-table-columns';
  payload: SelectedReferralsFilterColumnsData;
};

type ReceiveJobsOverviewTableColumnAction = {
  type: 'analytics-referrals/receive-jobs-overview-table-columns';
  payload: SelectedReferralsFilterColumnsData;
};

type ReceivePayoutsOverviewTableColumnAction = {
  type: 'analytics-referrals/receive-payouts-overview-table-columns';
  payload: SelectedReferralsFilterColumnsData;
};

export type AnalyticsReferralsActions =
  | SetDateRangeAction
  | ReceiveSavedDashboardsAction
  | SetCurrentDashboardIdAction
  | SetSelectedReferralFiltersAction
  | ClearSelectedFiltersAction
  | ReceiveReferralsOverviewTableColumnAction
  | ReceiveProgramsOverviewTableColumnAction
  | ReceiveJobsOverviewTableColumnAction
  | ReceivePayoutsOverviewTableColumnAction;

export const setDateRange = (
  payload: DateRangeWithTimezone,
): SetDateRangeAction => ({
  type: SET_SELECTED_DATE_RANGE,
  payload,
});

export const receiveSavedDashboards = (
  payload: SavedDashboardList,
): ReceiveSavedDashboardsAction => ({
  type: RECEIVE_SAVED_DASHBOARDS,
  payload,
});

export const setCurrentDashboardId = (
  payload: string,
): SetCurrentDashboardIdAction => ({
  type: SET_CURRENT_DASHBOARD_ID,
  payload,
});

export const setSelectedReferralFilters = (
  payload: CurrentReferralFilters,
): SetSelectedReferralFiltersAction => ({
  type: SET_SELECTED_REFERRALS_FILTERS,
  payload,
});

const receiveReferralsOverviewTableColumnSelection = (
  payload: SelectedReferralsFilterColumnsData,
): ReceiveReferralsOverviewTableColumnAction => ({
  type: RECEIVE_REFERRALS_OVERVIEW_TABLE_COLUMNS,
  payload,
});

const receiveProgramsOverviewTableColumnSelection = (
  payload: SelectedReferralsFilterColumnsData,
): ReceiveProgramsOverviewTableColumnAction => ({
  type: RECEIVE_PROGRAMS_OVERVIEW_TABLE_COLUMNS,
  payload,
});

const receiveJobsOverviewTableColumnSelection = (
  payload: SelectedReferralsFilterColumnsData,
): ReceiveJobsOverviewTableColumnAction => ({
  type: RECEIVE_JOBS_OVERVIEW_TABLE_COLUMNS,
  payload,
});

const receivePayoutsOverviewTableColumnSelection = (
  payload: SelectedReferralsFilterColumnsData,
): ReceivePayoutsOverviewTableColumnAction => ({
  type: RECEIVE_PAYOUTS_OVERVIEW_TABLE_COLUMNS,
  payload,
});

export const clearSelectedFilters = (): ClearSelectedFiltersAction => ({
  type: CLEAR_SELECTED_FILTERS,
});

export const getSavedReferralsDashboards: () => ThunkAction<unknown> =
  () => async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApi.get(
          `/referrals/save-filter`,
          {
            product: 'referrals',
            dashboard: 'referrals',
          },
          {apiPath: ANALYTICS_API_V3},
        ),
      );
      const {
        filters,
      }: {
        filters: SavedDashboardList;
      } = response;
      await dispatch(receiveSavedDashboards(filters));

      const defaultDashboard = filters.find(({is_default}) => is_default);
      if (defaultDashboard) {
        const {id = '', filters = {}} = defaultDashboard;
        const {
          // @ts-expect-error - TS2339 - Property 'job' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          job = [],
          // @ts-expect-error - TS2339 - Property 'program_name' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          program_name = [],
          // @ts-expect-error - TS2339 - Property 'source' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          source = [],
          // @ts-expect-error - TS2339 - Property 'team_member' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          team_member = [],
        } = filters;
        await dispatch(setCurrentDashboardId(id));
        await dispatch(
          setSelectedReferralFilters({
            job,
            program_name,
            source,
            team_member,
          }),
        );
      }
    } catch (error: any) {
      console.error('Error fetching saved dashboard filter:', error);
    }
  };

export const getReferralsOverviewTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getReferralOverviewTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedReferralsFilterColumnsData = await dispatch(
        reduxApi.get(
          '/referrals/column-selection',
          referralsOverviewTableColumnsQuery,
          analyticsOptions,
        ),
      );
      dispatch(receiveReferralsOverviewTableColumnSelection(payload));
    } catch (error: any) {
      console.error('Error fetching referrals overview column filter:', error);
    }
  });

export const getProgramsOverviewTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getProgramsOverviewTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedReferralsFilterColumnsData = await dispatch(
        reduxApi.get(
          '/referrals/column-selection',
          programsOverviewTableColumnsQuery,
          analyticsOptions,
        ),
      );
      dispatch(receiveProgramsOverviewTableColumnSelection(payload));
    } catch (error: any) {
      console.error('Error fetching programs overview column filter:', error);
    }
  });

export const getJobsOverviewTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getJobsOverviewTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedReferralsFilterColumnsData = await dispatch(
        reduxApi.get(
          '/referrals/column-selection',
          jobsOverviewTableColumnsQuery,
          analyticsOptions,
        ),
      );
      dispatch(receiveJobsOverviewTableColumnSelection(payload));
    } catch (error: any) {
      console.error('Error fetching jobs overview column filter:', error);
    }
  });

export const getPayoutsOverviewTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getPayoutsOverviewTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedReferralsFilterColumnsData = await dispatch(
        reduxApi.get(
          '/referrals/column-selection',
          payoutsOverviewTableColumnsQuery,
          analyticsOptions,
        ),
      );
      dispatch(receivePayoutsOverviewTableColumnSelection(payload));
    } catch (error: any) {
      console.error('Error fetching payouts overview column filter:', error);
    }
  });

export const saveReferralsFilterColumns: (
  columnData: SelectedReferralsFilterColumnsData,
) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/referrals/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);
